import type { CSSProperties } from 'react';
import type { TextStyleProps } from 'types';

function marginVal(v: number | 'auto') {
  return v === 'auto' ? 'auto' : `${v}px`;
}

function marginBlock(
  props: TextStyleProps,
  defaults: { b: string; t: string; l: string; r: string },
): Pick<
  CSSProperties,
  'margin' | 'marginBottom' | 'marginTop' | 'marginLeft' | 'marginRight'
> {
  if (props.margin !== undefined) {
    return {
      margin:
        typeof props.margin === 'number'
          ? `${props.margin}px`
          : props.margin,
    };
  }
  return {
    marginBottom:
      props.marginBottom !== undefined
        ? marginVal(props.marginBottom)
        : defaults.b,
    marginTop:
      props.marginTop !== undefined
        ? marginVal(props.marginTop)
        : defaults.t,
    marginLeft:
      props.marginLeft !== undefined
        ? marginVal(props.marginLeft)
        : defaults.l,
    marginRight:
      props.marginRight !== undefined
        ? marginVal(props.marginRight)
        : defaults.r,
  };
}

function paddingBlock(
  props: TextStyleProps,
  defaults: { b: string; t: string; l: string; r: string },
): Pick<
  CSSProperties,
  | 'padding'
  | 'paddingBottom'
  | 'paddingTop'
  | 'paddingLeft'
  | 'paddingRight'
> {
  if (props.padding !== undefined) {
    return { padding: `${props.padding}px` };
  }
  return {
    paddingBottom:
      props.paddingBottom !== undefined
        ? `${props.paddingBottom}px`
        : defaults.b,
    paddingTop:
      props.paddingTop !== undefined
        ? `${props.paddingTop}px`
        : defaults.t,
    paddingLeft:
      props.paddingLeft !== undefined
        ? `${props.paddingLeft}px`
        : defaults.l,
    paddingRight:
      props.paddingRight !== undefined
        ? `${props.paddingRight}px`
        : defaults.r,
  };
}

export function buildParagraphTextStyle(
  props: TextStyleProps,
): CSSProperties {
  const {
    color,
    hoverColor,
    fontWeight,
    textAlign,
    fontSize,
    letterSpacing,
  } = props;
  return {
    ...marginBlock(props, {
      b: '0.5rem',
      t: '0.5rem',
      l: '0.5rem',
      r: '0.5rem',
    }),
    ...paddingBlock(props, { b: '0', t: '0', l: '0', r: '0' }),
    fontFamily: "'Roboto', sans-serif",
    fontWeight: fontWeight ?? 400,
    textAlign: textAlign ?? 'left',
    fontSize: fontSize !== undefined ? `${fontSize}px` : '16px',
    letterSpacing:
      letterSpacing !== undefined ? `${letterSpacing}px` : undefined,
    ['--paragraph-color' as string]: color ?? '#000',
    ['--paragraph-hover' as string]: hoverColor ?? color ?? '#000',
  };
}

export function buildHeaderStyle(props: TextStyleProps): CSSProperties {
  const { color, fontWeight, textAlign, fontSize, lineHeight } = props;
  return {
    ...marginBlock(props, {
      b: '0.5rem',
      t: '0.5rem',
      l: '0.5rem',
      r: '0.5rem',
    }),
    ...paddingBlock(props, { b: '0', t: '0', l: '0', r: '0' }),
    color: color ?? '#000',
    fontWeight: fontWeight ?? 500,
    textAlign: textAlign ?? 'left',
    fontSize: fontSize !== undefined ? `${fontSize}px` : '26px',
    lineHeight: lineHeight !== undefined ? `${lineHeight}px` : '17px',
    fontFamily: "'Roboto', sans-serif",
  };
}

export function buildSubHeaderStyle(props: TextStyleProps): CSSProperties {
  const { color, fontWeight, textAlign, fontSize, lineHeight } = props;
  return {
    ...marginBlock(props, {
      b: '0.5rem',
      t: '0.5rem',
      l: '0.5rem',
      r: '0.5rem',
    }),
    ...paddingBlock(props, { b: '0', t: '0', l: '0', r: '0' }),
    color: color ?? '#000',
    fontWeight: fontWeight ?? 500,
    textAlign: textAlign ?? 'left',
    fontSize: fontSize !== undefined ? `${fontSize}px` : '17px',
    lineHeight: lineHeight !== undefined ? `${lineHeight}px` : '17px',
    fontFamily: "'Roboto', sans-serif",
  };
}

export function buildFormLabelStyle(props: TextStyleProps): CSSProperties {
  const { color, hoverColor, fontWeight, textAlign, fontSize } = props;
  return {
    ...marginBlock(props, {
      b: '.5rem',
      t: '.5rem',
      l: '.5rem',
      r: '.5rem',
    }),
    ...paddingBlock(props, { b: '0', t: '0', l: '0', r: '0' }),
    color: color ?? '#000',
    fontWeight: fontWeight ?? 400,
    textAlign: textAlign ?? 'left',
    fontSize: fontSize !== undefined ? `${fontSize}px` : '14px',
    fontFamily: "'Roboto', sans-serif",
    ['--label-hover' as string]: hoverColor ?? color ?? '#000',
  };
}
